/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import logger from './logger';

/**
 * A note that the visitor pressed Explore integrations on the login page,
 * rather than signing in to go somewhere in particular.
 *
 * The `EXPLORE_INTEGRATIONS_PATH` sentinel carries this through sign in on its
 * own for an organization that already exists. This exists for the one case the
 * sentinel cannot reach: a brand new organization, whose landing page is
 * decided by `CreateDefaultEnvs` once it has provisioned the Default project,
 * which never sees a `returnToUrl`.
 *
 * Stored the same way, and for the same reasons, as the pending Copilot prompt
 * next door in `copilotPrompt.ts` — see the note there on why it is
 * `localStorage`.
 */
const PENDING_KEY = 'devant_pending_explore_integrations';

/** An older note is ignored — the sign in was abandoned. */
const TTL_MS = 30 * 60 * 1000;

/**
 * Records the intent for the sign in round trip.
 *
 * Every access to `localStorage` is guarded: it throws outright in Safari
 * private mode and wherever the browser is set to block site data, and losing
 * this must never break signing in.
 */
export const setPendingExploreIntegrations = () => {
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch (error) {
    logger.error(
      'Failed to store the pending Explore integrations intent',
      error
    );
  }
};

export const clearPendingExploreIntegrations = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (error) {
    logger.error(
      'Failed to clear the pending Explore integrations intent',
      error
    );
  }
};

/**
 * Whether the intent is set and still fresh. An expired note is removed on the
 * way out.
 */
export const hasPendingExploreIntegrations = (): boolean => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_KEY);
  } catch (error) {
    logger.error(
      'Failed to read the pending Explore integrations intent',
      error
    );
    return false;
  }
  if (!raw) {
    return false;
  }

  const createdAt = Number(raw);
  if (!Number.isFinite(createdAt) || Date.now() - createdAt > TTL_MS) {
    clearPendingExploreIntegrations();
    return false;
  }

  return true;
};

/**
 * Reads the intent and removes it, so it is acted on exactly once. Every
 * consumer should use this rather than `has` followed by `clear`.
 */
export const consumePendingExploreIntegrations = (): boolean => {
  const isPending = hasPendingExploreIntegrations();
  if (isPending) {
    clearPendingExploreIntegrations();
  }
  return isPending;
};
